import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { graphql } from '~/graphql';
import { canonicalLengthIds } from '~/pages/order-builder/sections/lengthSelection';
import {
  runGraphqlQuery,
  STALE_TIME_MS,
  useGraphqlQuery,
} from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import type {
  BuyerInventoryFiltersInput,
  BuyerInventoryQuery,
  BuyerInventoryPriorityGroupsQuery,
  BuyerInventoryGroupLocationsQuery,
  SellerComparisonFiltersInput,
} from '~/graphql/graphql';
import { DeliveryMode, LogisticsIncoterm } from '~/graphql/graphql';
import { useQueries } from '@tanstack/react-query';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
export type BuyerInventoryQueryParams = {
  filters?: BuyerInventoryFiltersInput;
  includeInventoryLengths?: boolean;
};

export const useGetBuyerInventories = (
  page: number,
  size: number,
  params?: BuyerInventoryQueryParams,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const buyerInventoriesQuery = graphql(`
    query buyerInventory(
      $page: Int!
      $size: Int!
      $filters: BuyerInventoryFiltersInput
      $includeInventoryLengths: Boolean
      $sort: [SortInput!]
    ) {
      buyerInventory(
        pageInput: { page: $page, size: $size }
        filters: $filters
        includeInventoryLengths: $includeInventoryLengths
        sort: $sort
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          quantity {
            value
            unit
          }
          onOrderStock {
            unit
            value
          }
          availableStock {
            value
            unit
          }
          onHandStock {
            value
            unit
          }
          inboundStock {
            value
            unit
          }
          requestedStock {
            value
            unit
          }
          location {
            id
            code
            name
            facility {
              id
              code
              address {
                addressLine1
                city
                region
              }
              locations {
                branchCode
              }
            }
          }
          platformProductsLength {
            id
            isNominal
            lengthDisplayValue
            length {
              id
              displayValue
              nominalLengthInches
              actualLengthInches
            }
            platformProduct {
              id
              code
              name
              category
              properties
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
          buyerInventoryConfiguration {
            id
            usageRatePerWeek {
              value
              unit
            }
            weeksCoveredBeforeUrgent
            weeksCoveredBeforeMonitor
            targetWeeksCovered
          }
        }
      }
    }
  `);

  const filters = params?.filters;
  const includeInventoryLengths = params?.includeInventoryLengths;

  return useGraphqlQuery(
    buyerInventoriesQuery,
    [
      'buyerInventories',
      page.toString(),
      size.toString(),
      filters ?? null,
      includeInventoryLengths ?? false,
      sort ?? null,
    ],
    {
      page,
      size,
      filters,
      includeInventoryLengths,
      sort,
    },
    options
  );
};

// Dedicated probe for the buyer-inventory reveal CTA. Selecting totalElementsIncludingHidden is
// what makes the resolver run the extra "revealed" count, so it lives on its own page-size-1 query
// (counts only, no content) rather than the table queries — those must not pay the count per load.
export const useGetBuyerInventoryRevealCounts = (
  params?: BuyerInventoryQueryParams,
  options?: { enabled?: boolean }
) => {
  const revealCountsQuery = graphql(`
    query buyerInventoryRevealCounts($filters: BuyerInventoryFiltersInput) {
      buyerInventory(pageInput: { page: 0, size: 1 }, filters: $filters) {
        paging {
          totalElements
          totalElementsIncludingHidden
        }
      }
    }
  `);
  const filters = params?.filters;
  return useGraphqlQuery(
    revealCountsQuery,
    ['buyerInventoryRevealCounts', filters ?? null],
    { filters },
    { enabled: options?.enabled }
  );
};

export const PRIORITY_GROUPS_QUERY_KEY = 'buyerInventoryPriorityGroups';
export const GROUP_LOCATIONS_QUERY_KEY = 'buyerInventoryGroupLocations';

export function invalidatePriorityQueue(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [PRIORITY_GROUPS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GROUP_LOCATIONS_QUERY_KEY] });
}

export type BuyerInventoryPriorityGroupsQueryParams = {
  filters?: BuyerInventoryFiltersInput;
  includeInventoryLengths?: boolean;
};

const buyerInventoryPriorityGroupsQuery = graphql(`
  query buyerInventoryPriorityGroups(
    $page: Int!
    $size: Int!
    $filters: BuyerInventoryFiltersInput
    $includeInventoryLengths: Boolean
    $sort: [SortInput!]
  ) {
    buyerInventoryPriorityGroups(
      pageInput: { page: $page, size: $size }
      filters: $filters
      includeInventoryLengths: $includeInventoryLengths
      sort: $sort
    ) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      content {
        id
        facility {
          id
          code
          address {
            city
            region
          }
          locations {
            branchCode
          }
        }
        platformProductsLength {
          id
          isNominal
          lengthDisplayValue
          length {
            id
            displayValue
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            id
            code
            name
            category
            properties
            standardUnitsCode
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            piecesPerBundle {
              value
              unit
            }
          }
        }
        totalStock {
          value
          unit
        }
        totalOnOrderStock {
          value
          unit
        }
        totalAvailableStock {
          value
          unit
        }
        totalOnHandStock {
          value
          unit
        }
        totalInboundStock {
          value
          unit
        }
        totalRequestedStock {
          value
          unit
        }
        avgUsageRatePerWeek {
          value
          unit
        }
        targetInventory {
          id
          location {
            id
          }
          buyerInventoryConfiguration {
            id
            weeksCoveredBeforeUrgent
            weeksCoveredBeforeMonitor
          }
        }
      }
    }
  }
`);

export type BuyerInventoryRow = NonNullable<
  NonNullable<BuyerInventoryQuery['buyerInventory']>['content'][number]
>;

export type PriorityGroup = NonNullable<
  BuyerInventoryPriorityGroupsQuery['buyerInventoryPriorityGroups']
>['content'][number];

export const useGetBuyerInventoryPriorityGroups = (
  page: number,
  size: number,
  params?: BuyerInventoryPriorityGroupsQueryParams,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const filters = params?.filters;
  const includeInventoryLengths = params?.includeInventoryLengths;
  return useGraphqlQuery(
    buyerInventoryPriorityGroupsQuery,
    [
      PRIORITY_GROUPS_QUERY_KEY,
      page.toString(),
      size.toString(),
      filters ?? null,
      includeInventoryLengths ?? false,
      sort ?? null,
    ],
    { page, size, filters, includeInventoryLengths, sort },
    options
  );
};

const buyerInventoryGroupLocationsQuery = graphql(`
  query buyerInventoryGroupLocations(
    $facilityId: ID!
    $platformProductsLengthId: ID!
  ) {
    buyerInventoryGroupLocations(
      facilityId: $facilityId
      platformProductsLengthId: $platformProductsLengthId
    ) {
      location {
        id
        name
        code
        facility {
          id
          code
          address {
            addressLine1
            city
            region
          }
        }
      }
      inventory {
        id
        quantity {
          value
          unit
        }
        onOrderStock {
          value
          unit
        }
        availableStock {
          value
          unit
        }
        onHandStock {
          value
          unit
        }
        inboundStock {
          value
          unit
        }
        requestedStock {
          value
          unit
        }
        buyerInventoryConfiguration {
          id
          usageRatePerWeek {
            value
            unit
          }
          weeksCoveredBeforeUrgent
          weeksCoveredBeforeMonitor
          targetWeeksCovered
        }
      }
    }
  }
`);

export type GroupLocation =
  BuyerInventoryGroupLocationsQuery['buyerInventoryGroupLocations'][number];

// The identity `combine` is load-bearing: it makes useQueries return a referentially
// stable array until a query actually changes, instead of a fresh array per render.
const combineGroupLocationResults = (
  results: UseQueryResult<BuyerInventoryGroupLocationsQuery>[]
) => results;

export type GroupLocationKey = { facilityId: string; pplId: string };

// Result array is index-aligned with keys; each facility × product+length group
// keeps its own sub-row cache entry.
export const useGetBuyerInventoryGroupLocations = (
  keys: GroupLocationKey[]
) => {
  const queries = useMemo(
    () =>
      keys.map(({ facilityId, pplId }) => ({
        queryKey: [GROUP_LOCATIONS_QUERY_KEY, facilityId, pplId],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          runGraphqlQuery(
            buyerInventoryGroupLocationsQuery,
            { facilityId, platformProductsLengthId: pplId },
            signal
          ),
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
      })),
    [keys]
  );
  return useQueries({ queries, combine: combineGroupLocationResults });
};

export const useUpsertInventory = () => {
  const upsertInventoryMutation = graphql(`
    mutation upsertInventory(
      $platformProductsLengthId: ID!
      $locationId: ID!
      $quantity: QuantityInput!
    ) {
      upsertInventory(
        platformProductsLengthId: $platformProductsLengthId
        locationId: $locationId
        quantity: $quantity
      ) {
        id
        location {
          id
          name
        }
        platformProductsLength {
          id
          platformProduct {
            name
          }
        }
        buyerInventoryConfiguration {
          weeksCoveredBeforeMonitor
          weeksCoveredBeforeUrgent
          targetWeeksCovered
          location {
            id
          }
          platformProductsLength {
            id
          }
          usageRatePerWeek {
            value
            unit
          }
        }
        quantity {
          value
          unit
        }
        availableStock {
          value
          unit
        }
        onHandStock {
          value
          unit
        }
      }
    }
  `);
  return useGraphqlMutation(upsertInventoryMutation, [
    'buyerInventories',
    'uniqueLocationsWithInventory',
  ]);
};

export const useGetPlatformProductsLengthsForProduct = (
  platformProductId: string,
  options?: { enabled?: boolean }
) => {
  const platformProductsLengthsForProductQuery = graphql(`
    query platformProductsLengthsForProduct($platformProductId: ID!) {
      platformProductsLengthsForProduct(platformProductId: $platformProductId) {
        id
        isNominal
        inventoryOnly
        lengthDisplayValue
        platformProduct {
          id
          name
          standardUnitsCode
          nominalWidthInches
          actualWidthInches
          nominalThicknessInches
          actualThicknessInches
          actualLengthInches
          piecesPerBundle {
            value
            unit
          }
        }
        length {
          id
          actualLengthInches
          nominalLengthInches
        }
        latestPriceMax {
          amount
          currency
        }
        latestPriceMin {
          amount
          currency
        }
        latestPriceMid {
          amount
          currency
        }
        platformProductVehicleCapacities {
          transportVehicle {
            id
          }
          capacity {
            value
            unit
          }
        }
      }
    }
  `);
  return useGraphqlQuery(
    platformProductsLengthsForProductQuery,
    ['buyerInventories', platformProductId],
    {
      platformProductId: platformProductId,
    },
    { enabled: options?.enabled }
  );
};
export type SellerProductInventoriesForBuyerFilters = {
  platformProductLengthIds?: string[];
  platformProductId?: string | null;
  sellerOrganizationId?: string | null;
  destinationLocationId?: string | null;
  deliveryMode?: DeliveryMode | null;
  includeInventoryLengths?: boolean | null;
  comparisonFilters?: SellerComparisonFiltersInput | null;
};

export const vendorGroupCartKey = (
  platformProductLengthId: string,
  organizationId: string
): string => [platformProductLengthId, organizationId].join('-');

export const sortedLengthIdsCacheKey = (
  platformProductLengthIds?: string[] | null
): string =>
  platformProductLengthIds?.length
    ? canonicalLengthIds(platformProductLengthIds).join(',')
    : 'none';

// Exported so useVendorGroupFacilities can reuse this exact document instead of duplicating it.
export const sellerProductInventoriesForBuyerQuery = graphql(`
  query sellerProductInventoriesForBuyer(
    $page: Int!
    $size: Int!
    $platformProductLengthIds: [ID!]
    $platformProductId: ID
    $sellerOrganizationId: ID
    $destinationLocationId: ID
    $deliveryMode: DeliveryMode
    $includeInventoryLengths: Boolean
    $comparisonFilters: SellerComparisonFiltersInput
    $sort: [SortInput!]
  ) {
    sellerProductInventoriesForBuyer(
      pageInput: { page: $page, size: $size }
      filter: {
        platformProductLengthIds: $platformProductLengthIds
        platformProductId: $platformProductId
        sellerOrganizationId: $sellerOrganizationId
        destinationLocationId: $destinationLocationId
        deliveryMode: $deliveryMode
        includeInventoryLengths: $includeInventoryLengths
      }
      comparisonFilters: $comparisonFilters
      sort: $sort
    ) {
      paging {
        totalElements
        totalPages
        number
        size
      }
      recommendedPrice {
        amount
        currency
      }
      recommendedPriceCount
      sellersCount
      content {
        id
        quantity {
          value
          unit
        }
        unitPrice {
          price {
            amount
            currency
          }
          unit
        }
        deliveredShippingRate(destinationLocationId: $destinationLocationId) {
          amount
          currency
        }
        pickupShippingRate(destinationLocationId: $destinationLocationId) {
          amount
          currency
        }
        facility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
            country
          }
          organization {
            id
            name
            sellingOrganizationCode
          }
        }
        platformProductsLength {
          id
          isNominal
          inventoryOnly
          lengthDisplayValue
          length {
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            id
            name
            code
            nominalWidthInches
            actualWidthInches
            nominalThicknessInches
            actualThicknessInches
            actualLengthInches
            standardUnitsCode
            piecesPerBundle {
              value
              unit
            }
          }
        }
        sellerProductConfiguration {
          id
          minimumOrderQuantity {
            value
            unit
          }
          readyToShipDays
          facility {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
              country
            }
          }
          organization {
            id
            name
            sellingOrganizationCode
          }
          unit
        }
      }
    }
  }
`);

export const useGetSellerProductInventoriesForBuyer = (
  page: number,
  pageSize: number,
  filters?: SellerProductInventoriesForBuyerFilters,
  sort?: RouteSort,
  options?: { enabled?: boolean }
) => {
  const normalizedDestinationLocationId =
    filters?.destinationLocationId?.trim() || undefined;
  return useGraphqlQuery(
    sellerProductInventoriesForBuyerQuery,
    [
      'sellerProductInventories',
      page,
      pageSize,
      sortedLengthIdsCacheKey(filters?.platformProductLengthIds),
      filters?.platformProductId ?? 'none',
      filters?.sellerOrganizationId ?? 'none',
      normalizedDestinationLocationId ?? 'none',
      filters?.deliveryMode ?? 'none',
      filters?.includeInventoryLengths ?? false,
      JSON.stringify(filters?.comparisonFilters ?? {}),
      sort ?? null,
    ],
    {
      page,
      size: pageSize,
      platformProductLengthIds: filters?.platformProductLengthIds,
      platformProductId: filters?.platformProductId,
      sellerOrganizationId: filters?.sellerOrganizationId,
      destinationLocationId: normalizedDestinationLocationId,
      deliveryMode: filters?.deliveryMode ?? undefined,
      includeInventoryLengths: filters?.includeInventoryLengths ?? undefined,
      comparisonFilters: filters?.comparisonFilters ?? undefined,
      sort,
    },
    { placeholderData: keepPreviousData, enabled: options?.enabled }
  );
};

export const useGetSellerVendorInventoryGroupsForBuyer = (
  page: number,
  pageSize: number,
  filters?: SellerProductInventoriesForBuyerFilters,
  sort?: RouteSort,
  options?: { enabled?: boolean }
) => {
  const sellerVendorInventoryGroupsForBuyerQuery = graphql(`
    query SellerVendorInventoryGroupsForBuyer(
      $page: Int!
      $size: Int!
      $platformProductLengthIds: [ID!]
      $platformProductId: ID
      $sellerOrganizationId: ID
      $destinationLocationId: ID
      $deliveryMode: DeliveryMode
      $includeInventoryLengths: Boolean
      $comparisonFilters: SellerComparisonFiltersInput
      $sort: [SortInput!]
    ) {
      sellerVendorInventoryGroupsForBuyer(
        pageInput: { page: $page, size: $size }
        filter: {
          platformProductLengthIds: $platformProductLengthIds
          platformProductId: $platformProductId
          sellerOrganizationId: $sellerOrganizationId
          destinationLocationId: $destinationLocationId
          deliveryMode: $deliveryMode
          includeInventoryLengths: $includeInventoryLengths
        }
        comparisonFilters: $comparisonFilters
        sort: $sort
      ) {
        content {
          id
          organization {
            id
            name
            sellingOrganizationCode
            defaultCurrency
            facilities {
              id
              code
              locations {
                id
                code
              }
            }
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            inventoryOnly
          }
          priceRange {
            min {
              amount
              currency
            }
            max {
              amount
              currency
            }
          }
          totalStock {
            value
            unit
          }
          minReadyToShipDays
          facilityCount
          hasPricedFacility
        }
        paging {
          number
          size
          totalElements
          totalPages
        }
        recommendedPrice {
          amount
          currency
        }
        recommendedPriceCount
        sellersCount
      }
    }
  `);
  const normalizedDestinationLocationId =
    filters?.destinationLocationId?.trim() || undefined;
  return useGraphqlQuery(
    sellerVendorInventoryGroupsForBuyerQuery,
    [
      'sellerVendorInventoryGroupsForBuyer',
      page,
      pageSize,
      sortedLengthIdsCacheKey(filters?.platformProductLengthIds),
      filters?.platformProductId ?? 'none',
      filters?.sellerOrganizationId ?? 'none',
      normalizedDestinationLocationId ?? 'none',
      filters?.deliveryMode ?? 'none',
      filters?.includeInventoryLengths ?? false,
      JSON.stringify(filters?.comparisonFilters ?? {}),
      sort ?? null,
    ],
    {
      page,
      size: pageSize,
      platformProductLengthIds: filters?.platformProductLengthIds,
      platformProductId: filters?.platformProductId,
      sellerOrganizationId: filters?.sellerOrganizationId,
      destinationLocationId: normalizedDestinationLocationId,
      deliveryMode: filters?.deliveryMode ?? undefined,
      includeInventoryLengths: filters?.includeInventoryLengths ?? undefined,
      comparisonFilters: filters?.comparisonFilters ?? undefined,
      sort,
    },
    { placeholderData: keepPreviousData, enabled: options?.enabled }
  );
};

export const useGetBuyerProductInventory = (id: string) => {
  const inventoryQuery = graphql(`
    query buyerProductInventory($id: ID!) {
      buyerProductInventory(id: $id) {
        id
        quantity {
          value
          unit
        }
        availableStock {
          value
          unit
        }
        onHandStock {
          value
          unit
        }
        location {
          id
          name
          facility {
            address {
              city
              region
            }
          }
        }
        platformProductsLength {
          id
          lengthDisplayValue
          platformProduct {
            id
            name
            code
            actualThicknessInches
            actualWidthInches
            finishCode
            constructionCode
            properties
          }
        }
        buyerInventoryConfiguration {
          id
          usageRatePerWeek {
            value
            unit
          }
          weeksCoveredBeforeUrgent
          weeksCoveredBeforeMonitor
          targetWeeksCovered
        }
      }
    }
  `);

  return useGraphqlQuery(inventoryQuery, ['inventory', id], {
    id: id,
  });
};

export const useGetBuyerInventoryByProductAndLocation = (
  lengthId: string,
  platformProductId: string,
  locationId: string
) => {
  const query = graphql(`
    query buyerInventoryByProductAndLocation(
      $lengthId: ID!
      $platformProductId: ID!
      $locationId: ID!
    ) {
      buyerInventoryByProductAndLocation(
        lengthId: $lengthId
        platformProductId: $platformProductId
        locationId: $locationId
      ) {
        id
        quantity {
          value
          unit
        }
        availableStock {
          value
          unit
        }
        onHandStock {
          value
          unit
        }
        onOrderStock {
          value
          unit
        }
        inboundStock {
          value
          unit
        }
        location {
          id
          name
          facility {
            address {
              city
              region
            }
          }
        }
        platformProductsLength {
          id
          isNominal
          lengthDisplayValue
          length {
            id
            nominalLengthInches
            actualLengthInches
          }
          platformProduct {
            id
            name
            code
            nominalThicknessInches
            actualThicknessInches
            nominalWidthInches
            actualWidthInches
            actualLengthInches
            finishCode
            constructionCode
            properties
          }
        }
        buyerInventoryConfiguration {
          id
          usageRatePerWeek {
            value
            unit
          }
          weeksCoveredBeforeUrgent
          weeksCoveredBeforeMonitor
          targetWeeksCovered
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['inventoryByProductAndLocation', lengthId, platformProductId, locationId],
    { lengthId, platformProductId, locationId },
    { enabled: !!lengthId && !!platformProductId && !!locationId }
  );
};

export const useGetAdditionalProductsFromSeller = (cartId: string) => {
  const additionalItemsQuery = graphql(`
    query additionalProductsFromSeller($orderId: ID!, $pageInput: PageInput!) {
      additionalProductsFromSeller(orderId: $orderId, pageInput: $pageInput) {
        content {
          id
          quantity(mode: { mode: USER_PREFERRED }) {
            value
            unit
          }
          unitPrice {
            price {
              amount
              currency
            }
            unit
          }
          platformProductsLength {
            id
            isNominal
            lengthDisplayValue
            length {
              nominalLengthInches
              actualLengthInches
            }
            platformProduct {
              id
              name
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              standardUnitsCode
              properties
              piecesPerBundle {
                value
              }
            }
            platformProductVehicleCapacities {
              transportVehicle {
                id
              }
              capacity {
                value
                unit
              }
            }
          }
          weeksCovered
          stockHealth
        }
        paging {
          totalElements
          totalPages
          number
          size
        }
      }
    }
  `);

  return useGraphqlQuery(
    additionalItemsQuery,
    ['additionalItems', cartId],
    { orderId: cartId, pageInput: { page: 0, size: 3 } },
    { enabled: !!cartId }
  );
};

export const useGetSellerLowStockProducts = () => {
  const query = graphql(`
    query GetSellerLowStockProducts {
      sellerLowStockProducts {
        id
        health
        platformProductLength {
          lengthDisplayValue
          platformProduct {
            id
            name
          }
        }
        lowStockLevel {
          value
          unit
        }
      }
    }
  `);

  // Cache key is singleton (no user discriminator). This is safe because the
  // backend scopes results to the authenticated session — the server never
  // returns another user's data. The cache is invalidated on logout via a
  // full page reload, so stale data from a prior session cannot leak.
  return useGraphqlQuery(query, ['sellerLowStockProducts'], undefined, {
    refetchOnMount: 'always',
  });
};

const buyerInventoriesByLengthsQuery = graphql(`
  query buyerInventoriesByProductAndLocationForTally(
    $lengthIds: [ID!]!
    $platformProductId: ID!
    $locationId: ID!
  ) {
    buyerInventoriesByProductAndLocation(
      lengthIds: $lengthIds
      platformProductId: $platformProductId
      locationId: $locationId
    ) {
      quantity {
        value
        unit
      }
      buyerInventoryConfiguration {
        usageRatePerWeek {
          value
          unit
        }
        weeksCoveredBeforeUrgent
        weeksCoveredBeforeMonitor
        targetWeeksCovered
      }
    }
  }
`);

function dropPlaceholderLengthIds(lengthIds: string[]) {
  const filteredLengthIds: string[] = [];
  const originalIndexByFilteredIndex: number[] = [];
  lengthIds.forEach((lengthId, originalIndex) => {
    if (lengthId) {
      originalIndexByFilteredIndex.push(originalIndex);
      filteredLengthIds.push(lengthId);
    }
  });
  return { filteredLengthIds, originalIndexByFilteredIndex };
}

export const useGetBuyerInventoriesForLengths = (
  lengthIds: string[],
  platformProductId: string | undefined,
  locationId: string | null | undefined
) => {
  const { filteredLengthIds, originalIndexByFilteredIndex } =
    dropPlaceholderLengthIds(lengthIds);

  const variables =
    filteredLengthIds.length > 0 && platformProductId && locationId
      ? { lengthIds: filteredLengthIds, platformProductId, locationId }
      : undefined;

  const { data, isLoading } = useGraphqlQuery(
    buyerInventoriesByLengthsQuery,
    [
      'inventoriesByProductAndLocationForTally',
      filteredLengthIds.join(','),
      platformProductId ?? '',
      locationId ?? '',
    ],
    variables,
    { enabled: !!variables }
  );

  const inventoryByOriginalIndex = new Map<
    number,
    NonNullable<
      NonNullable<typeof data>['buyerInventoriesByProductAndLocation']
    >[number]
  >();
  data?.buyerInventoriesByProductAndLocation?.forEach(
    (inventory, filteredIndex) => {
      if (inventory) {
        inventoryByOriginalIndex.set(
          originalIndexByFilteredIndex[filteredIndex],
          inventory
        );
      }
    }
  );

  return lengthIds.map((_, originalIndex) => {
    const inventory = inventoryByOriginalIndex.get(originalIndex);
    return {
      data: inventory
        ? { buyerInventoryByProductAndLocation: inventory }
        : undefined,
      isLoading,
    };
  });
};

const sellerPriceForBuyerQuery = graphql(`
  query sellerPriceForBuyer(
    $platformProductLengthId: ID!
    $sellerOrganizationId: ID
    $facilityId: ID
    $deliveryMode: DeliveryMode
    $destinationLocationId: ID
  ) {
    sellerProductInventoriesForBuyer(
      pageInput: { page: 0, size: 1 }
      filter: {
        platformProductLengthIds: [$platformProductLengthId]
        sellerOrganizationId: $sellerOrganizationId
        facilityId: $facilityId
        deliveryMode: $deliveryMode
        destinationLocationId: $destinationLocationId
      }
    ) {
      content {
        unitPrice {
          price {
            amount
            currency
          }
          unit
        }
      }
    }
  }
`);

/** The price a seller Facility offers the buyer for a length, as on the price comparison. */
export const useGetSellerPriceForBuyer = (
  platformProductLengthId: string | undefined,
  sellerOrganizationId: string,
  facilityId: string,
  incoTerm: LogisticsIncoterm | null | undefined,
  destinationLocationId: string
) => {
  const deliveryMode =
    incoTerm === LogisticsIncoterm.Exw
      ? DeliveryMode.Pickup
      : DeliveryMode.Delivered;
  const { data, isLoading, isError } = useGraphqlQuery(
    sellerPriceForBuyerQuery,
    [
      'sellerPriceForBuyer',
      platformProductLengthId ?? 'none',
      sellerOrganizationId,
      facilityId,
      deliveryMode,
      destinationLocationId,
    ],
    {
      platformProductLengthId: platformProductLengthId ?? '',
      sellerOrganizationId,
      facilityId,
      deliveryMode,
      destinationLocationId,
    },
    { enabled: !!platformProductLengthId }
  );
  const unitPrice =
    data?.sellerProductInventoriesForBuyer.content[0]?.unitPrice;
  return {
    price: unitPrice?.price
      ? {
          amount: unitPrice.price.amount,
          currency: unitPrice.price.currency,
          unit: unitPrice.unit,
        }
      : null,
    isLoading,
    isError,
  };
};
